import { Testimonials } from "../components/Testimonials";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowRight, CheckCircle, Users, Target, TrendingUp, Globe, Star, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";

export default function AboutPage() {
  const companyValues = [
    {
      icon: Target,
      title: "Excellence",
      description: "We strive for perfection in every project, delivering solutions that exceed expectations and drive measurable business results."
    },
    {
      icon: Heart,
      title: "Integrity", 
      description: "Honest communication, transparent processes, and ethical business practices form the foundation of all our client relationships."
    },
    {
      icon: Users,
      title: "Collaboration",
      description: "We work as an extension of your team, fostering partnerships that create lasting value and mutual success."
    },
    {
      icon: TrendingUp,
      title: "Innovation",
      description: "Constantly evolving our expertise and methodologies to deliver cutting-edge solutions using the latest technologies."
    },
    {
      icon: Globe,
      title: "Impact",
      description: "Making a meaningful difference in businesses worldwide by transforming operations and accelerating growth."
    },
    {
      icon: Star,
      title: "Quality",
      description: "Rigorous quality assurance processes ensure every solution we deliver meets the highest standards of performance."
    }
  ];

  const teamMembers = [
    {
      name: "Rajesh Kumar",
      role: "Founder & CEO",
      expertise: "Zoho Ecosystem Strategy",
      experience: "8+ years",
      certifications: ["Zoho Partner Certified", "Business Strategy Expert"],
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face"
    },
    {
      name: "Priya Sharma",
      role: "Lead Technical Architect", 
      expertise: "Zoho CRM & Analytics",
      experience: "6+ years",
      certifications: ["Zoho CRM Expert", "Analytics Specialist"],
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=300&h=300&fit=crop&crop=face"
    },
    {
      name: "Amit Patel",
      role: "Senior Developer",
      expertise: "Zoho Creator & Integrations",
      experience: "5+ years", 
      certifications: ["Creator Registered", "API Integration Expert"],
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face"
    },
    {
      name: "Sarah Wilson",
      role: "Client Success Manager",
      expertise: "Project Management & Training",
      experience: "4+ years",
      certifications: ["PMP Certified", "Zoho Training Specialist"],
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face"
    }
  ];

  const caseStudies = [
    {
      company: "TechStart Solutions",
      industry: "Technology",
      challenge: "Disconnected systems causing data silos and inefficient processes",
      solution: "Complete Zoho One implementation with custom integrations",
      results: ["300% increase in productivity", "50% reduction in operational costs", "90% improvement in data accuracy"],
      image: "https://images.unsplash.com/photo-1553484771-371a605b060b?w=400&h=250&fit=crop"
    },
    {
      company: "RetailPro Inc", 
      industry: "E-commerce",
      challenge: "Manual inventory management and order processing bottlenecks",
      solution: "Zoho Inventory + Commerce integration with automated workflows",
      results: ["80% faster order processing", "95% inventory accuracy", "40% increase in sales"],
      image: "https://images.unsplash.com/photo-1556742393-d75f468bfcb0?w=400&h=250&fit=crop"
    },
    {
      company: "ServiceCorp Ltd",
      industry: "Professional Services",
      challenge: "Poor customer service response times and ticket management",
      solution: "Zoho Desk implementation with MyOperator integration",
      results: ["70% faster response times", "95% customer satisfaction", "60% increase in ticket resolution"],
      image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=400&h=250&fit=crop"
    }
  ];

  const milestones = [
    { year: "2019", title: "Company Founded", description: "Started with a vision to simplify business operations" },
    { year: "2020", title: "Zoho Partnership", description: "Became official Zoho Authorized Partner" },
    { year: "2021", title: "100+ Projects", description: "Achieved first major milestone in project delivery" },
    { year: "2022", title: "Creator Certification", description: "Earned Zoho Creator Registered Partner status" },
    { year: "2023", title: "International Expansion", description: "Extended services to global markets" },
    { year: "2024", title: "500+ Success Stories", description: "Celebrating over 500 successful implementations" }
  ];

  const futureGoals = [
    {
      title: "Excellence in Service",
      description: "Continuously improving our service delivery to exceed client expectations",
      icon: Target
    },
    {
      title: "Innovation Leadership", 
      description: "Leading the industry with cutting-edge Zoho automation solutions",
      icon: TrendingUp
    },
    {
      title: "Global Expansion",
      description: "Expanding our reach to serve businesses worldwide",
      icon: Globe
    },
    {
      title: "Client Success",
      description: "Helping more businesses achieve digital transformation success",
      icon: Heart
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-24 lg:pt-28 pb-16 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-800">About Optiwizer</Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 mb-6">
                Transforming Businesses with
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 block">
                  Zoho Excellence
                </span>
              </h1>
              <p className="text-lg lg:text-xl text-gray-600 mb-8 leading-relaxed">
                Since 2019, we've been helping businesses unlock their potential through expert Zoho implementations, 
                automation solutions, and strategic consulting. Our mission is to make technology work seamlessly 
                for your business growth.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-4">
                    Work With Us
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Button size="lg" variant="outline" className="text-lg px-8 py-4">
                  Download Company Profile
                </Button>
              </div>
            </div>

            <div className="relative">
              <ImageWithFallback 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop"
                alt="Optiwizer team collaboration"
                className="rounded-2xl shadow-xl w-full h-[300px] lg:h-[400px] object-cover"
              />
              
              {/* Floating Achievement Cards */}
              <div className="absolute -top-4 -right-4 lg:-top-6 lg:-right-6 bg-white rounded-xl shadow-lg p-4">
                <div className="text-2xl lg:text-3xl font-bold text-blue-600">500+</div>
                <div className="text-sm text-gray-600">Projects</div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 lg:-bottom-6 lg:-left-6 bg-green-500 text-white rounded-xl shadow-lg p-4">
                <div className="text-xl lg:text-2xl font-bold">98%</div>
                <div className="text-sm">Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <Badge className="mb-4 bg-purple-100 text-purple-800">Our Values</Badge>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What Drives Us Every Day
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Our core values shape every interaction, decision, and solution we deliver, 
              ensuring consistent excellence and meaningful business impact.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {companyValues.map((value, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6 lg:p-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
                    <value.icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <Badge className="mb-4 bg-green-100 text-green-800">Our Team</Badge>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Meet the Experts Behind Your Success
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Our certified team of Zoho specialists brings deep expertise, proven methodologies, 
              and passionate commitment to every project we undertake.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {teamMembers.map((member, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="mb-6">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                    />
                    <h3 className="font-bold text-gray-900 mb-1">{member.name}</h3>
                    <div className="text-sm text-blue-600 font-medium mb-1">{member.role}</div>
                    <div className="text-sm text-gray-500">{member.experience}</div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <div className="text-xs font-medium text-gray-700 mb-1">Expertise</div>
                      <div className="text-xs text-gray-600">{member.expertise}</div>
                    </div>
                    
                    <div>
                      <div className="text-xs font-medium text-gray-700 mb-2">Certifications</div>
                      <div className="space-y-1">
                        {member.certifications.map((cert, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {cert}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline / Milestones */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-800">Our Journey</Badge>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Key Milestones in Our Growth Story
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              From humble beginnings to becoming a trusted Zoho partner, 
              here's how we've grown alongside our clients' success.
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-blue-200 hidden lg:block"></div>
            
            <div className="space-y-8 lg:space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`relative flex items-center ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}>
                  {/* Timeline dot */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full border-4 border-white shadow-lg hidden lg:block z-10"></div>
                  
                  <div className={`w-full lg:w-5/12 ${index % 2 === 0 ? 'lg:pr-8' : 'lg:pl-8'}`}> 
                    <Card className="hover:shadow-lg transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="flex items-center mb-3">
                          <div className="w-3 h-3 bg-blue-600 rounded-full mr-3 lg:hidden"></div>
                          <Badge className="bg-blue-100 text-blue-800">{milestone.year}</Badge>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {milestone.title}
                        </h3>
                        <p className="text-gray-600">
                          {milestone.description}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div className="hidden lg:block lg:w-5/12"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <Badge className="mb-4 bg-green-100 text-green-800">Success Stories</Badge>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Real Results for Real Businesses
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how our Zoho expertise has transformed operations and 
              accelerated growth for businesses across different industries.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {caseStudies.map((study, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="relative">
                  <img
                    src={study.image}
                    alt={study.company}
                    className="w-full h-48 object-cover rounded-t-lg"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-white text-gray-800">{study.industry}</Badge>
                  </div>
                </div>
                
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    {study.company}
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Challenge</h4>
                      <p className="text-sm text-gray-600">{study.challenge}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Solution</h4>
                      <p className="text-sm text-gray-600">{study.solution}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Results</h4>
                      <ul className="space-y-1">
                        {study.results.map((result, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0 mt-1" />
                            <span className="text-xs text-gray-600">{result}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Future Goals & Vision */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-800">Our Vision</Badge>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Building the Future of Business Automation
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Our commitment to excellence drives us forward as we continue to innovate 
              and help businesses worldwide achieve their digital transformation goals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {futureGoals.map((goal, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mb-4">
                    <goal.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-3">{goal.title}</h3>
                  <p className="text-sm text-gray-600">{goal.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Ready to be Part of Our Success Story?
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Join hundreds of businesses that have already transformed their operations with our expert Zoho solutions. 
                Let's discuss how we can help accelerate your business growth and operational efficiency.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
                    Start Your Journey
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button size="lg" variant="outline" className="border-2">
                    Explore Our Services
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-r from-gray-900 to-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Join Our Success Stories?
          </h2>
          <p className="text-lg lg:text-xl text-gray-300 mb-8">
            Let's discuss how our proven expertise and passionate team can help 
            transform your business operations and accelerate your growth journey.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-4">
                Start Your Transformation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8 py-4">
                Explore Our Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}